import { Building2 } from 'lucide-react';

import { EmptyState } from '@/components/shared/EmptyState';
import { OrganizationCard } from '@/features/orgs/components/OrganizationCard';
import { SkeletonBlock } from '@/components/common/SkeletonBlock';
import type { Organization } from '@/features/orgs/types';

export interface OrganizationGridProps {
  organizations: Organization[];
  /** Design §9 copy differs by one word from the dashboard's §8 copy. */
  emptyTitle: string;
  /** Number of skeleton cards while loading (design: 6, matched to the grid). */
  skeletonCount?: number;
  className?: string;
}

/**
 * Card grid shared by the dashboard section (§8) and the organizations list (§9)
 * — same card, same 1/2/3-column collapse, same loading skeleton and empty state
 * (H3). Loading and error treatments stay with the caller because each page owns
 * its own query.
 */
export function OrganizationGrid({
  organizations,
  emptyTitle,
  skeletonCount = 6,
  className,
}: OrganizationGridProps) {
  if (organizations.length === 0) {
    return (
      <EmptyState
        icon={<Building2 />}
        title={emptyTitle}
        description="Ask an administrator to add you to an organization."
      />
    );
  }

  return (
    <div className={className ?? 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3'}>
      {organizations.map((organization) => (
        <OrganizationCard key={organization._id} organization={organization} />
      ))}
    </div>
  );
}

/** Six `h-32` card placeholders, `aria-hidden` (design §16.1). */
export function OrganizationGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
      aria-hidden="true"
      data-testid="organization-grid-skeleton"
    >
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonBlock key={index} className="h-32" />
      ))}
    </div>
  );
}
